/**
 * @typedef {Object} ProjectCard
 * @property {string} heading
 * @property {string[]} [categories] - Used by the Works section filter tabs
 * @property {string} description
 * @property {string} [animationDelay] - Legacy; no longer used by the UI
 * @property {string[]} [tags]
 * @property {string} buttonLink - Live `https://` URL or `#` for internal-only work
 * @property {string} buttonText
 */

/**
 * @typedef {Object} ExperienceProject
 * @property {string} name
 * @property {string} summary
 * @property {string[]} points
 */

/**
 * @typedef {Object} ExperienceEntry
 * @property {string} role
 * @property {string} company
 * @property {string} [companyUrl]
 * @property {string} location
 * @property {string} [note] - e.g. contracting arrangement
 * @property {string} period
 * @property {string[]} stack
 * @property {ExperienceProject[]} projects
 */

/**
 * Site content: projects, experience, etc.
 * Forkers: edit this file (or split into smaller modules) to personalize the site.
 */
const portfolioConfig = {
  portfolio: {
    portfolioLogo: 'Portfolio',
    projectCard: {
      /** @type {ProjectCard[]} */
      cards: [
        {
          heading: 'Sprint DCS - Web-Based Departure Control System',
          categories: ['Aviation'],
          description:
            'Migrated flydubai\'s Departure Control System from a legacy desktop app to a 100% web platform covering check-in, boarding, load control, flight control, and real-time reporting, improving first-load performance and passenger processing speed by 50%. Designed a microfrontend architecture, streamed live gate data with Kafka, optimized for low-bandwidth airports with Akamai CDN and caching, and integrated OpenAI APIs to auto-generate flight and incident summaries.',
          animationDelay: '900',
          tags: ['Next.js', 'Angular', 'Microfrontends', 'MobX', 'OpenAI', 'Internal'],
          buttonLink: '#',
          buttonText: 'Not accessible to regular users',
        },
        {
          heading: 'BMS - Baggage Management System',
          categories: ['Aviation'],
          description:
            'Built a web-based Baggage Management System inspired by SITA WorldTracer for tracking baggage and handling delayed and found baggage cases. Implemented AHL (delayed) and OHD (on-hand) integration for matching, case creation, and resolution; end-to-end tracking across every checkpoint (check-in, sortation, loading, transfer, arrival); and BSM/BPM event processing to keep baggage data in sync across systems. Built internal resolution logic that resolves 80% of cases from flydubai\'s own data without querying WorldTracer, cutting baggage-tracing costs by 80%.',
          animationDelay: '900',
          tags: ['TypeScript', 'Aviation', 'Real-time', 'BSM / BPM', 'Internal'],
          buttonLink: '#',
          buttonText: 'Not accessible to regular users',
        },
        {
          heading: 'Self Check-in Kiosk - Flydubai Airline',
          categories: ['Aviation'],
          description:
            'Developed an Angular-based multilingual self-service check-in kiosk supporting 15+ languages, enabling passengers to self check-in, select seats, purchase services, scan passports, print baggage tags, and complete payments. Integrated multiple aviation backend systems for passenger verification and payment handling, and built a responsive kiosk UI that reduces friction for international passengers.',
          animationDelay: '900',
          tags: ['Angular', 'Kiosk', 'i18n · 15+ languages', 'Internal'],
          buttonLink: '#',
          buttonText: 'Not accessible to regular users',
        },
        {
          heading: 'Ezboarding - Mobile-Based Departure Control System',
          categories: ['Aviation', 'Mobile'],
          description:
            'Built a mobile-based Departure Control System (DCS) for airport gate agents to manage flight, passenger, and boarding operations. Achieved a boarding efficiency of over 40 passengers per minute and integrated real-time flight data and passenger management for seamless mobile operations.',
          animationDelay: '900',
          tags: ['Mobile', 'DCS', 'Real-time', 'Internal'],
          buttonLink: '#',
          buttonText: 'Not accessible to regular users',
        },
        {
          heading: 'Grattan - fashion e-commerce',
          categories: ['E-commerce'],
          description:
            'E-commerce for clothing and fashion accessories built on Microsoft Dynamics 365 Commerce. Led the development of the Product Cart page, improving user experience and achieving 70% unit test coverage, and integrated One Link Payment for seamless checkout and payment processing.',
          animationDelay: '900',
          tags: ['React', 'TypeScript', 'Dynamics 365', 'Jest', 'E-commerce'],
          buttonLink:
            'https://www.grattan.co.uk/?realestate=siteheaderlinks&linkname=logo',
          buttonText: 'CHECK OUT',
        },
        {
          heading: 'Krannich - solar equipment e-commerce',
          categories: ['E-commerce'],
          description:
            'E-commerce for solar and related equipment built with React.js, TypeScript, MobX, Bootstrap, and Microsoft Dynamics 365 Commerce on Azure Retail Server. Leveraged CRM to build and customize multiple pages aligned with client requirements, and implemented unit tests with Jest and React Testing Library, achieving 90% code coverage.',
          animationDelay: '900',
          tags: ['React', 'TypeScript', 'MobX', 'Azure Commerce'],
          buttonLink: 'https://aus-mb.krannich-shop.com/',
          buttonText: 'CHECK OUT',
        },
        {
          heading: 'Shipox - SaaS for Delivery Management',
          categories: ['Logistics', 'Platforms'],
          description:
            'SaaS for delivery management with real-time tracking, used by 60k+ businesses worldwide. Implemented 1,300+ role-based permissions using React.js, enhancing user access control and security, and built 10+ reusable components, improving code reusability by 60%.',
          animationDelay: '300',
          tags: ['React', 'Redux', 'SaaS', 'RBAC'],
          buttonLink: 'https://shipox.com/',
          buttonText: 'CHECK OUT',
        },
        {
          heading: 'Meri Gari - Logistics Platform',
          categories: ['Logistics'],
          description:
            'Logistics platform connecting corporations and transporters, managing 8k+ trips monthly. Developed backend logic for trip workflows, transporter matching, and vehicle assignment, and designed a Redis caching proof of concept to reduce API latency and improve performance.',
          animationDelay: '500',
          tags: ['React', 'Node.js', 'Redis', 'Logistics'],
          buttonLink: 'https://merigari.com',
          buttonText: 'CHECK OUT',
        },
        {
          heading: 'Trukkr - Online Logistics Platform',
          categories: ['Logistics'],
          description:
            'Pakistani start-up (6M USD funding) and the first online logistics platform connecting carriers with shippers. Created and integrated 30+ REST APIs (including Master APIs) for real-time tracking, trip management, and user services, and developed features with React.js and Redux that improved usability and UI responsiveness across modules.',
          animationDelay: '700',
          tags: ['React', 'Redux', 'Node.js', 'REST APIs', 'Logistics'],
          buttonLink: 'https://trukkr.pk/',
          buttonText: 'CHECK OUT',
        },
        {
          heading:
            'Trukkr Ada - Mobile Platform to connect corporations and transporters',
          categories: ['Logistics', 'Mobile'],
          description:
            'Developed and integrated 100% of the backend APIs end-to-end for mobile app flows covering trip acceptance, bidding, and vehicle/driver assignment. Deployed the app to the Google Play Store and set up an automated testing environment for improved reliability.',
          animationDelay: '900',
          tags: ['React Native', 'Node.js', 'REST APIs', 'Jest'],
          buttonLink:
            'https://play.google.com/store/apps/details?id=com.trukkrPak.transporter',
          buttonText: 'CHECK OUT',
        },
        {
          heading: 'The Contract Master - Procurement Web Platform',
          categories: ['Platforms'],
          description:
            'Contributed to The Contract Master, an online platform for businesses to manage contracts. Developed features, wrote unit tests with 80% coverage, and deployed the app using AWS S3.',
          animationDelay: '900',
          tags: ['React', 'AWS S3', 'Jest', 'Procurement'],
          buttonLink: 'https://www.thecontractmaster.com/',
          buttonText: 'CHECK OUT',
        },
        {
          heading: 'The Contract Master - Procurement Mobile Platform',
          categories: ['Platforms', 'Mobile'],
          description:
            'Contributed to The Contract Master, an online platform for businesses to manage contracts. Developed the complete app using React Native, TypeScript, and Native Base with Zustand for vendors and suppliers.',
          animationDelay: '900',
          tags: ['React Native', 'TypeScript', 'Native Base', 'Zustand'],
          buttonLink:
            'https://play.google.com/store/search?q=the%20contract%20master&c=apps',
          buttonText: 'CHECK OUT',
        },
        {
          heading: 'Gamitar - Game Based Learning',
          categories: ['Platforms', 'Mobile'],
          description:
            'Contributed to the development of a game-based learning platform, creating end-to-end game experiences for mobile and web. Also designed an admin portal for facilitators, making gamified learning easy for Fortune 500 companies and global facilitators.',
          animationDelay: '900',
          tags: ['Game-based learning', 'Web & mobile', 'Admin portal'],
          buttonLink: 'https://www.gamitar.com/',
          buttonText: 'CHECK OUT',
        },
        {
          heading:
            'Pro Bono Zone™ - Connecting. Empowering. Elevating Lives.',
          categories: ['Platforms'],
          description:
            'Contributed to creating complete landing pages, a portal, and a backend server on Node.js and SQL, deployed on DigitalOcean. Pro Bono Zone™ connects individuals with seasoned attorneys, offering free consultations in a safe, private, and supportive environment. Created an intuitive website with logo, home, about, FAQ, sponsors, and contact sections.',
          animationDelay: '900',
          tags: ['Node.js', 'SQL', 'Full stack', 'DigitalOcean'],
          buttonLink: 'https://pro-bone-zone.vercel.app/',
          buttonText: 'CHECK OUT',
        },
      ],
    },
    /**
     * Work history, newest first. Shown in the About section and on `/about/experience`.
     * @type {ExperienceEntry[]}
     */
    experience: [
      {
        role: 'Software Engineer',
        company: 'flydubai',
        companyUrl: 'https://www.flydubai.com/',
        location: 'Dubai, UAE',
        period: '10/2025 – 10/2026',
        stack: ['TypeScript', 'Next.js', 'Angular', 'Microfrontend', 'MobX', 'Material UI', 'Localization', 'Jest', 'Cypress', 'Playwright', 'Kafka', 'AWS', 'Akamai', 'Monorepo', 'Azure DevOps', 'SonarQube', 'Checkmarx', 'OpenAPI (Swagger)'],
        projects: [
          {
            name: 'Sprint DCS (Departure Control System)',
            summary:
              'Secure web-based Departure Control System for airport gates covering check-in, boarding, load control, and real-time reporting.',
            points: [
              'Designed and implemented a microfrontend architecture with shared, reusable UI modules, improving scalability and maintainability of a complex aviation platform',
              'Migrated the DCS from a legacy desktop application to a 100% web-based platform, improving first-load performance by 50% and passenger processing speed by 50%',
              'Optimized front-end performance for low-bandwidth airport environments using caching, Akamai CDN integration, and image optimization',
              'Built a real-time boarding interface for passengers, flight data, and gate operations using Kafka event streams, and enhanced flight control, load control, and reporting modules',
              'Integrated OpenAI APIs to auto-generate flight and incident summaries, and used GitHub Copilot daily to speed up feature delivery',
              'Led code reviews and mentored developers, maintaining quality with Jest unit tests, Cypress/Playwright end-to-end tests, and SonarQube/Checkmarx checks',
            ],
          },
          {
            name: 'BMS (Baggage Management System)',
            summary:
              'Web-based Baggage Management System inspired by SITA WorldTracer for baggage tracking and delayed/found baggage cases.',
            points: [
              "Built end-to-end baggage tracking views across every checkpoint (check-in, sortation, loading, transfer, arrival), giving real-time visibility of each bag's status and location",
              'Designed and implemented the AHL (delayed baggage) and OHD (on-hand baggage) integration for matching, case creation, and resolution of mishandled baggage',
              'Implemented Kafka-based event processing for BSM and BPM baggage messages, keeping data accurate and in sync across systems',
              "Built internal resolution logic that resolves 80% of cases using flydubai's own data without querying SITA WorldTracer, reducing baggage-tracing costs by 80%",
              'Worked directly with business stakeholders and product owners to turn operational needs into clear functional specifications',
            ],
          },
        ],
      },
      {
        role: 'Senior Software Engineer',
        company: 'Coforge FZ LLC',
        companyUrl: 'https://www.coforge.com/',
        location: 'Dubai, UAE',
        note: 'Contracted for flydubai',
        period: '03/2024 – 10/2025',
        stack: ['TypeScript', 'Next.js', 'Angular', 'Microfrontend', 'Redux', 'Material UI', 'Localization', 'AWS', 'Jest', 'Cypress', 'Playwright'],
        projects: [
          {
            name: 'Ezboarding',
            summary:
              'Mobile-based Departure Control System (DCS) for airport gate agents to manage flights, passengers, and high-speed boarding.',
            points: [
              'Achieved a boarding throughput of over 40 passengers per minute by building a fast, mobile-first boarding application',
              'Integrated real-time flight data and passenger management features for seamless mobile operations',
              'Mentored 5 developers and conducted regular code reviews across the team',
            ],
          },
          {
            name: 'Self Check-in Kiosk',
            summary: 'Angular kiosk app for passenger self-check-in, seats, services, bag tags, and payment.',
            points: [
              'Developed a multilingual self-service kiosk supporting 15+ languages, including passport scanning, baggage tagging, and payment processing',
              'Built a responsive, touch-friendly UI that reduced interaction friction for international passengers',
              'Integrated multiple aviation backend systems for passenger verification and payment handling',
            ],
          },
        ],
      },
      {
        role: 'Consultant, Dynamics 365 Commerce',
        company: 'Systems LTD',
        companyUrl: 'https://www.systemsltd.com/',
        location: 'Pakistan',
        note: 'Transferred internally to Tech Vista, UAE',
        period: '06/2023 – 02/2024',
        stack: ['TypeScript', 'React.js', 'Dynamics 365 Commerce', 'MobX', 'Bootstrap', 'Jest', 'React Testing Library', 'Azure Retail Server', 'Azure'],
        projects: [
          {
            name: 'Grattan',
            summary: 'E-commerce for clothing and fashion accessories.',
            points: [
              'Led development of the Product Cart page in React.js and TypeScript, improving user experience and achieving 70% unit test coverage',
              'Integrated One Link Payment to enable seamless checkout and payment processing',
            ],
          },
          {
            name: 'Krannich',
            summary: 'E-commerce for solar and related equipment.',
            points: [
              'Built and customized multiple React.js/TypeScript pages driven by CRM data, aligned with client requirements',
              'Implemented unit testing using Jest and React Testing Library, achieving 90% code coverage',
            ],
          },
        ],
      },
      {
        role: 'Senior Frontend Developer',
        company: 'Next Generation Innovation',
        companyUrl: 'https://nextgeni.com/',
        location: 'Karachi, Pakistan',
        period: '01/2022 – 05/2023',
        stack: ['JavaScript', 'React.js', 'Next.js', 'Redux', 'Redux Saga', 'RxJS', 'Material UI', 'Webpack', 'Node.js', 'PostgreSQL', 'Docker', 'Jest', 'GitLab', 'Bitbucket', 'Jira', 'Twilio'],
        projects: [
          {
            name: 'Shipox',
            summary:
              'SaaS for delivery management with real-time tracking, available in English and Arabic (RTL), used by 60k+ businesses worldwide.',
            points: [
              'Implemented 1,300+ role-based permissions in React.js, enhancing user access control and security',
              'Built 10+ reusable React components, improving code reusability by 60%',
            ],
          },
          {
            name: 'Meri Gari',
            summary: 'Logistics platform connecting corporations and transporters, managing 8k+ trips monthly.',
            points: [
              'Developed trip workflow, transporter matching, and vehicle assignment logic, improving system efficiency and scalability',
              'Built a Redis caching proof of concept that reduced API latency and improved application performance',
            ],
          },
        ],
      },
      {
        role: 'Senior Frontend Developer',
        company: 'Trukkr Pvt Limited',
        companyUrl: 'https://trukkr.ai/',
        location: 'Karachi, Pakistan',
        period: '07/2019 – 01/2022',
        stack: ['JavaScript', 'React.js', 'Redux', 'React Native', 'Node.js', 'Firebase', 'Jest', 'Google Maps', 'Java', 'Spring Boot', 'RabbitMQ'],
        projects: [
          {
            name: 'Trukkr',
            summary:
              'Pakistani start-up (6M USD funding) and the first online logistics platform connecting carriers with shippers, with a fintech product offering loans to drivers.',
            points: [
              'Developed and enhanced features using React.js and Redux, improving platform usability and UI responsiveness across modules',
              'Created and integrated 30+ REST APIs (including Master APIs), enabling real-time tracking, trip management, and user services',
              'Independently built a Java Spring Boot microservice for notifications and event-driven jobs using RabbitMQ',
            ],
          },
          {
            name: 'Trukkr Ada',
            summary: 'Mobile app platform connecting corporations and transporters.',
            points: [
              'Developed and integrated 100% of the backend APIs supporting mobile flows for trip acceptance, bidding, and vehicle/driver assignment',
              'Deployed the mobile application to the Google Play Store and established an automated testing environment',
            ],
          },
        ],
      },
    ],
  },
};

export default portfolioConfig;
